<template>
    <InfoBaseForm
        v-if="formData"
        ref="baseFormRef"
        v-model="formData"
        :is-loading="isLoading"
        @save="save()"
        @cancel="emit('cancel')"
    >
        <template v-slot:center-block>
            <v-row dense class="mb-4">
                <v-col cols="12" sm="6" md="4">
                    <v-autocomplete
                        v-if="serviceType !== contractServiceType.ownVisa"
                        v-model="formData.serviceTypeId"
                        :items="secondaryTypes"
                        :label="`${$t('type')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="type"
                        item-title="typeName"
                        item-value="id"
                        :rules="[requiredRule('type')]"
                    />
                    <v-checkbox
                        v-model="formData.defaultService"
                        :label="$t('product.info.default')"
                        density="compact"
                        color="primary"
                        data-cy="is-default"
                        :disabled="formData.defaultService && editSecondaryService"
                        hide-details
                    />
                </v-col>
            </v-row>
        </template>
        <template
            v-if="
                (serviceType === contractServiceType.tour ||
                    serviceType === contractServiceType.customComplexTour ||
                    serviceType === contractServiceType.ownCruise) &&
                formData?.tariffLinks?.length
            "
            v-slot:bottom-block
        >
            <div class="text-subtitle-1">{{ $t('use_for_tariffs') }}</div>
            <v-row dense>
                <v-col cols="12" md="8">
                    <v-data-table-server
                        :headers="useHeaders"
                        :items="formData.tariffLinks"
                        :items-per-page="-1"
                        :items-per-page-options="[-1]"
                        :items-length="formData.tariffLinks.length"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 table-server mb-10"
                        data-cy="guests-table"
                        hide-default-footer
                    >
                        <template v-slot:item.name="{item}">
                            {{ getTariffName(item.tariffId) }}
                        </template>
                        <template v-slot:item.typeSearchResults="{item}">
                            <v-checkbox
                                v-model="item.typeSearchResults"
                                hide-details
                                density="compact"
                                data-cy="type-search-results"
                                @update:model-value="updateOfferServiceTypes(item.tariffId)"
                            />
                        </template>
                        <template v-slot:item.typeAlternalive="{item}">
                            <v-checkbox
                                v-model="item.typeAlternalive"
                                hide-details
                                density="compact"
                                data-cy="type-alternative"
                                @update:model-value="updateOfferServiceTypes(item.tariffId)"
                            />
                        </template>
                        <template v-slot:item.typeNotOffer="{item}">
                            <v-checkbox
                                v-model="item.typeNotOffer"
                                hide-details
                                density="compact"
                                data-cy="type-not-offer"
                                @update:model-value="updateServiceTypes(item.tariffId)"
                            />
                        </template>
                    </v-data-table-server>
                </v-col>
            </v-row>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {initInfoSecondaryService} from '~/utils/types/account/products/helpers'
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import {contractServiceType} from '~/utils/types/types'
    import {clone} from '~/utils/helpers'

    const props = defineProps<{
        editSecondaryService: any | null
        contractId?: number
        serviceType?: string
        secondaryTypes?: any[]
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const formData = ref(initInfoSecondaryService())
    const baseFormRef = ref(null)
    const isTariffsLoading = ref<boolean>(false)
    const tariffs = ref([])

    onMounted(async () => {
        if (
            props.serviceType === contractServiceType.tour ||
            props.serviceType === contractServiceType.ownCruise ||
            props.serviceType === contractServiceType.customComplexTour
        ) {
            EventBus.$on(CHANGE_LOCALE_EVENT, reload)
            await loadTariffs()
            if (!props.editSecondaryService?.id) {
                formData.value = {
                    ...formData.value,
                    ...{tariffLinks: []},
                }

                formData.value.tariffLinks = tariffs.value.map(({id}) => ({
                    tariffId: id,
                    typeSearchResults: true,
                    typeAlternalive: false,
                    typeNotOffer: false,
                }))
            }
        }

        if (props.editSecondaryService) formData.value = clone(props.editSecondaryService)
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reload)
    })

    async function reload(): Promise<void> {
        await loadTariffs()
    }

    async function loadTariffs() {
        try {
            isTariffsLoading.value = true
            tariffs.value = (await app.$api.tariffs.get({contractId: props.contractId})).tariffs
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isTariffsLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        const data = clone(formData.value)
        clean(data)
        try {
            isLoading.value = true
            if (props.editSecondaryService?.id) {
                delete data.categoryId
                await app.$api.ownProductSecondaryServices.put({
                    id: props.editSecondaryService.id,
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownProductSecondaryServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('offer_on_search_result'), value: 'typeSearchResults'},
            {title: t('offer_as_alternative'), value: 'typeAlternalive'},
            {title: t('not_available'), value: 'typeNotOffer'},
        ]
    })

    const updateServiceTypes = id => {
        const index = formData.value.tariffLinks.findIndex(({tariffId}) => tariffId === id)
        formData.value.tariffLinks[index] = {
            ...formData.value.tariffLinks[index],
            typeSearchResults: false,
            typeAlternalive: false,
            typeNotOffer: formData.value.tariffLinks[index].typeNotOffer,
        }
    }

    const updateOfferServiceTypes = id => {
        const index = formData.value.tariffLinks.findIndex(({tariffId}) => tariffId === id)
        formData.value.tariffLinks[index] = {
            ...formData.value.tariffLinks[index],
            typeSearchResults: formData.value.tariffLinks[index].typeSearchResults,
            typeAlternalive: formData.value.tariffLinks[index].typeAlternalive,
            typeNotOffer: false,
        }
    }

    const getTariffName = tariffId => {
        const tariff = tariffs.value.find(({id}) => tariffId === id)
        return tariff?.name || tariff?.id
    }
</script>
